/**
 * Types and pure helpers for the route change history.
 *
 * Deliberately free of any Supabase import, following `lib/routeExports.ts`: `@/lib/supabase`
 * builds a client at module load and throws when the VITE_ env vars are absent, as they are in CI.
 * Anything a test needs to reach lives on this side of that line; `hooks/useChangeEvents` is the
 * side that talks to the database.
 *
 * The row shape is written out here rather than taken from `database.types.ts` because the two
 * serve different purposes: this is the subset the UI reads, and naming it here keeps the panel
 * from depending on a regenerated file to compile.
 */
import { differenceInMinutes, format, isValid, parseISO } from "date-fns"

import { formatDayMonth, formatDayMonthYear, formatDayShort } from "./dateFormats"
import type { RouteExport } from "./routeExports"
import type { Tone } from "./tone"

export type ChangeScope = "plan" | "instance"
export type ChangeCause = "direct" | "cascade" | "backfill"
export type ChangeStatus = "applied" | "partial" | "failed"
export type ActorKind = "user" | "api_key" | "cron" | "system" | "unknown"

/** One side of a field's movement, as stored in the before/after payloads. */
export type FieldValue = string | number | boolean | string[] | null

export interface ChangeEvent {
  id: string
  occurred_at: string
  correlation_id: string
  sequence_in_action: number
  parent_event_id: string | null
  event_type: string
  scope: ChangeScope
  cause: ChangeCause
  status: ChangeStatus
  error: string | null
  actor_id: string | null
  actor_email: string | null
  actor_name: string | null
  actor_kind: ActorKind
  actor_verified: boolean
  planned_route_id: string | null
  planned_route_code: string | null
  route_instance_id: string | null
  route_instance_code: string | null
  route_instance_date: string | null
  entity_table: string
  entity_id: string | null
  entity_label: string | null
  before: Record<string, FieldValue> | null
  after: Record<string, FieldValue> | null
  is_full_snapshot: boolean
  changed_fields: string[]
  summary: string
  impact_from: string | null
  impact_to: string | null
  impacted_dates: string[] | null
  impacted_instance_count: number
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/**
 * What each event type is called on screen.
 *
 * Written from the reader's point of view rather than the table's: "Tuesday removed" is what the
 * planner did, `plan.schedule_days_changed` is where it went. An unmapped type falls back to a
 * humanised form of its own name rather than to "Modified", so a taxonomy addition shows up as
 * something readable instead of silently becoming indistinguishable from everything else.
 */
const EVENT_LABELS: Record<string, string> = {
  "plan.created": "Plan created",
  "plan.updated": "Plan updated",
  "plan.renamed": "Plan renamed",
  "plan.status_changed": "Plan status changed",
  "plan.retired": "Plan retired",
  "plan.restored": "Plan restored",
  "plan.schedule_days_changed": "Schedule days changed",
  "plan.effective_dates_changed": "Effective dates changed",
  "plan.excluded_dates_changed": "Excluded dates changed",
  "plan.times_changed": "Route times changed",
  "plan.vehicle_type_changed": "Vehicle type changed",
  "plan.operator_allocated": "Handed to another operator",
  "plan.operator_allocation_removed": "Handover removed",
  "plan.deleted": "Plan deleted",
  "plan.cascade_applied": "Changes applied to future dates",
  "plan_stops.synced": "Stops changed",
  "plan_stop.added": "Stop added to plan",
  "plan_stop.removed": "Stop removed from plan",
  "plan_stop.resequenced": "Stop reordered",
  "plan_stop.retimed": "Stop retimed",
  "plan_stop.updated": "Stop updated",
  "instance.created": "Route created",
  "instance.updated": "Route updated",
  "instance.renamed": "Route renamed",
  "instance.retimed": "Route retimed",
  "instance.reassigned": "Driver or vehicle reassigned",
  "instance.vehicle_type_changed": "Vehicle type changed",
  "instance.status_changed": "Status changed",
  "instance.cancelled": "Route cancelled",
  "instance.locked": "Route locked",
  "instance.unlocked": "Route unlocked",
  "instance.duplicated": "Route duplicated",
  "instance.deleted": "Route deleted",
  "stop.added": "Stop added",
  "stop.removed": "Stop removed",
  "stop.resequenced": "Stop reordered",
  "stop.retimed": "Stop retimed",
  "stop.skipped": "Stop skipped",
  "stop.unskipped": "Stop unskipped",
  "stop.updated": "Stop updated",
}

export function changeEventLabel(eventType: string): string {
  const known = EVENT_LABELS[eventType]
  if (known) return known
  const tail = eventType.split(".").pop() ?? eventType
  const words = tail.replace(/_/g, " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The semantic tone for an event, never a Tailwind class — the rule `lib/tone.ts` exists to
 * enforce.
 *
 * Cancellations and deletions are `danger` because they remove work from the plan, which is the
 * thing a planner most needs to spot on a scan. Creations and restorations are `success`.
 * Everything else is `info`, and a failed or partial event overrides all of it.
 */
export function changeEventTone(event: Pick<ChangeEvent, "event_type" | "status">): Tone {
  if (event.status === "failed") return "danger"
  if (event.status === "partial") return "warning"

  const type = event.event_type
  if (
    type.endsWith(".cancelled") ||
    type.endsWith(".deleted") ||
    type.endsWith(".removed") ||
    type === "plan.retired"
  ) {
    return "danger"
  }
  if (type.endsWith(".created") || type === "plan.restored" || type === "instance.duplicated") {
    return "success"
  }
  if (type.endsWith(".skipped")) return "warning"
  return "info"
}

/** An icon key the component maps to an SVG, so this module stays free of React. */
export type ChangeEventIcon =
  | "created"
  | "removed"
  | "retimed"
  | "reordered"
  | "renamed"
  | "person"
  | "vehicle"
  | "calendar"
  | "lock"
  | "stop"
  | "cascade"
  | "export"
  | "generic"

export function changeEventIcon(eventType: string): ChangeEventIcon {
  if (eventType.endsWith(".created") || eventType === "instance.duplicated") return "created"
  if (
    eventType.endsWith(".cancelled") ||
    eventType.endsWith(".deleted") ||
    eventType.endsWith(".removed")
  ) {
    return "removed"
  }
  if (eventType.endsWith(".retimed") || eventType === "plan.times_changed") return "retimed"
  if (eventType.endsWith(".resequenced")) return "reordered"
  if (eventType.endsWith(".renamed")) return "renamed"
  if (eventType === "instance.reassigned" || eventType.startsWith("plan.operator_allocat")) {
    return "person"
  }
  if (eventType.endsWith(".vehicle_type_changed")) return "vehicle"
  if (
    eventType === "plan.schedule_days_changed" ||
    eventType === "plan.effective_dates_changed" ||
    eventType === "plan.excluded_dates_changed"
  ) {
    return "calendar"
  }
  if (eventType === "instance.locked" || eventType === "instance.unlocked") return "lock"
  if (eventType.startsWith("stop.") || eventType.startsWith("plan_stop")) return "stop"
  if (eventType === "plan.cascade_applied" || eventType === "plan.restored") return "cascade"
  return "generic"
}

// ---------------------------------------------------------------------------
// Field formatting
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, string> = {
  planned_arrival: "Arrival",
  planned_departure: "Departure",
  planned_start_time: "Start",
  planned_end_time: "End",
  default_start_time: "Start",
  default_end_time: "End",
  arrival_day_offset: "Arrival day",
  departure_day_offset: "Departure day",
  service_time_minutes: "Service time",
  sequence: "Position",
  days: "Days",
  effective_from: "Effective from",
  effective_to: "Effective to",
  excluded_dates: "Excluded dates",
  driver_id: "Driver id",
  driver_name: "Driver",
  second_driver_id: "Second driver id",
  second_driver_name: "Second driver",
  vehicle_reg: "Vehicle",
  vehicle_type: "Vehicle type",
  crew_size: "Crew size",
  estimated_cost: "Cost",
  is_locked: "Locked",
  is_skipped: "Skipped",
  is_active: "Active",
  stop_type: "Stop type",
  stop_services: "Services",
  stop_pnl: "P&L",
  slot_services: "Services",
  slot_pnl: "P&L",
  haulage_delivery_revenue_gbp: "Revenue",
  direct_injection_cost_gbp: "Direct injection cost",
  code: "Code",
  name: "Name",
  status: "Status",
  depot: "Depot",
  operator: "Operator",
  category: "Category",
  notes: "Notes",
  modification_notes: "Modification notes",
}

export function fieldLabel(field: string): string {
  const known = FIELD_LABELS[field]
  if (known) return known
  const words = field.replace(/_/g, " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const TIME_ONLY = /^(\d{2}):(\d{2})(:\d{2})?$/

/** Minutes since midnight for an `HH:mm[:ss]` string, or null when it is not one. */
function timeOnlyMinutes(value: FieldValue): number | null {
  if (typeof value !== "string") return null
  const match = TIME_ONLY.exec(value)
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2])
}

function asDate(value: FieldValue): Date | null {
  if (typeof value !== "string") return null
  if (TIME_ONLY.test(value)) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

/**
 * One field's movement, ready to render.
 *
 * `delta` is only set where a signed minute count is meaningful — two timestamps or two
 * times-of-day. A sequence going from 4 to 5 is not "+1 minute".
 */
export interface FormattedChange {
  field: string
  label: string
  before: string
  after: string
  deltaMinutes: number | null
}

export function formatValue(value: FieldValue): string {
  if (value === null || value === undefined) return "—"
  if (Array.isArray(value)) return value.length ? value.join(", ") : "none"
  if (typeof value === "boolean") return value ? "yes" : "no"
  if (typeof value === "number") return String(value)

  if (TIME_ONLY.test(value)) return value.slice(0, 5)
  const parsed = asDate(value)
  if (parsed) {
    // Date-only strings carry no clock, so showing 00:00 would invent precision.
    return /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? formatDayMonthYear(parsed)
      : format(parsed, "HH:mm")
  }
  return value
}

export function formatFieldChange(
  field: string,
  before: FieldValue,
  after: FieldValue
): FormattedChange {
  let deltaMinutes: number | null = null

  const beforeMinutes = timeOnlyMinutes(before)
  const afterMinutes = timeOnlyMinutes(after)
  if (beforeMinutes !== null && afterMinutes !== null) {
    deltaMinutes = afterMinutes - beforeMinutes
  } else {
    const beforeDate = asDate(before)
    const afterDate = asDate(after)
    // Date-only values are excluded: a two-day shift in an effective date is not a minute count.
    const dateOnly = (v: FieldValue) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)
    if (beforeDate && afterDate && !dateOnly(before) && !dateOnly(after)) {
      deltaMinutes = differenceInMinutes(afterDate, beforeDate)
    }
  }

  return {
    field,
    label: fieldLabel(field),
    before: formatValue(before),
    after: formatValue(after),
    deltaMinutes,
  }
}

/** Whether the whole row appeared or disappeared, rather than fields within it moving. */
export function isRowLifecycle(event: ChangeEvent): boolean {
  return !event.before || !event.after
}

/** Opaque identifiers. Stored, because a revert needs them; never rendered, because nobody reads a uuid. */
const NEVER_RENDERED = new Set([
  "id",
  "route_id",
  "route_instance_id",
  "planned_stop_id",
  "planned_route_id",
  "location_id",
  "availability_slot_id",
  "updated_by",
  "created_by",
  "notes",
  // The prose workaround this log replaces. "Modified on 8/27/2026 - stop times updated" says
  // less than the events either side of it, in a format nothing can query.
  "modification_notes",
])

/**
 * Fields whose movement the event's own label already states.
 *
 * "Route locked" followed by `Locked: no → yes` is the same fact twice, and a reader scanning a
 * column of headlines does not need the restatement underneath each one.
 */
const RESTATED_BY_LABEL: Record<string, string[]> = {
  "instance.locked": ["is_locked", "is_modified"],
  "instance.unlocked": ["is_locked", "is_modified"],
  "instance.cancelled": ["status"],
  "plan.retired": ["status"],
  "stop.skipped": ["is_skipped"],
  "stop.unskipped": ["is_skipped"],
}

/**
 * The identity of a row that was added or removed, in one line.
 *
 * Ordered on purpose, and short: for a removed stop the questions are which stop, when was it, and
 * where in the route -- not the twenty-two other columns it happened to carry.
 */
const LIFECYCLE_FIELDS: Record<string, string[]> = {
  collections_stop_instances: ["name", "planned_arrival", "planned_departure", "sequence"],
  collections_route_stops: ["planned_arrival", "planned_departure", "sequence", "stop_type"],
  collections_route_instances: ["code", "date", "driver_name", "vehicle_reg"],
  collections_routes_planned: ["code", "days", "default_start_time", "default_end_time"],
}

/**
 * What an added or removed row was, as a single readable line.
 *
 * A lifecycle event has no before-and-after to show: the whole row is the change. Rendering it as
 * a field diff produced a wall of twenty-two struck-through rows, most of them uuids and nulls,
 * for what a reader needed one sentence to understand. The full row is still stored -- this is a
 * decision about what to show, not what to keep.
 */
export function rowSummary(event: ChangeEvent): string | null {
  if (!isRowLifecycle(event)) return null
  const row = event.after ?? event.before
  if (!row) return null

  const present = (field: string) => {
    const value = row[field]
    return value === null || value === undefined || value === "" ? null : value
  }

  const parts: string[] = []
  for (const field of LIFECYCLE_FIELDS[event.entity_table] ?? []) {
    // Arrival and departure read as one span, in the position arrival occupies.
    if (field === "planned_departure" && present("planned_arrival")) continue
    if (field === "planned_arrival") {
      const arrival = present("planned_arrival")
      const departure = present("planned_departure")
      if (arrival && departure) {
        parts.push(
          `${formatValue(arrival as FieldValue)} – ${formatValue(departure as FieldValue)}`
        )
        continue
      }
    }

    const value = present(field)
    if (value === null) continue
    parts.push(
      field === "sequence"
        ? `position ${formatValue(value as FieldValue)}`
        : formatValue(value as FieldValue)
    )
  }

  return parts.length ? parts.join(" · ") : null
}

/**
 * The field movements worth showing for an event.
 *
 * Driven by `changed_fields` rather than by walking the payloads, so the set matches what the
 * capture layer decided actually moved. Three things are dropped: opaque identifiers, `driver_id`
 * when `driver_name` is present (they always move together and the id is not what anyone reads),
 * and any field whose two sides render the same -- a `— → —` row is noise dressed as information.
 *
 * A row that was added or removed yields nothing here; see `rowSummary`.
 */
export function formattedChanges(event: ChangeEvent): FormattedChange[] {
  if (isRowLifecycle(event)) return []
  const fields = event.changed_fields ?? []
  const hasName = fields.includes("driver_name")
  const restated = new Set(RESTATED_BY_LABEL[event.event_type] ?? [])
  return fields
    .filter((f) => !NEVER_RENDERED.has(f))
    .filter((f) => !restated.has(f))
    .filter((f) => !(f === "driver_id" && hasName))
    .map((f) => formatFieldChange(f, event.before?.[f] ?? null, event.after?.[f] ?? null))
    .filter((c) => c.before !== c.after)
}

// ---------------------------------------------------------------------------
// Impact
// ---------------------------------------------------------------------------

export type ImpactAction =
  "created" | "cancelled" | "retimed" | "reassigned" | "reclassed" | "renamed" | "changed"

export interface ImpactChip {
  date: string
  label: string
  action: ImpactAction
  tone: Tone
}

const ACTION_FROM_EVENT: Record<string, ImpactAction> = {
  "instance.created": "created",
  "instance.cancelled": "cancelled",
  "instance.deleted": "cancelled",
  "instance.retimed": "retimed",
  "instance.reassigned": "reassigned",
  "instance.vehicle_type_changed": "reclassed",
  "instance.renamed": "renamed",
  "instance.updated": "changed",
}

const ACTION_TONE: Record<ImpactAction, Tone> = {
  created: "success",
  cancelled: "danger",
  retimed: "info",
  reassigned: "info",
  reclassed: "info",
  renamed: "neutral",
  changed: "info",
}

/**
 * The date chips for a plan event, built from the child events it caused.
 *
 * Derived from the children rather than from the parent's `impacted_dates` because the children
 * carry *what happened* to each date, and "three Tuesdays cancelled" is a different fact from
 * "three dates affected". Falls back to the parent's date list when no children are loaded, so a
 * collapsed row still says something true.
 */
export function impactChips(parent: ChangeEvent, children: ChangeEvent[]): ImpactChip[] {
  // An instance event's impact is its own date, always — so a chip saying "1 Sep" on a change to
  // the 1 Sep route restates the row it sits in. The strip earns its place only on a plan event,
  // where the question "which dates did this decision reach" has an answer worth reading.
  if (parent.scope === "instance") return []

  const byDate = new Map<string, ImpactAction>()

  for (const child of children) {
    if (!child.route_instance_date) continue
    const action = ACTION_FROM_EVENT[child.event_type] ?? "changed"
    // Removal outranks a same-date edit: a cancelled day is the headline.
    const existing = byDate.get(child.route_instance_date)
    if (existing === "cancelled") continue
    byDate.set(child.route_instance_date, action)
  }

  if (byDate.size === 0) {
    for (const date of parent.impacted_dates ?? []) byDate.set(date, "changed")
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, action]) => {
      const parsed = parseISO(date)
      return {
        date,
        label: isValid(parsed) ? formatDayMonth(parsed) : date,
        action,
        tone: ACTION_TONE[action],
      }
    })
}

/**
 * How far forward an event's window reaches, in words.
 *
 * The open-ended case is the one that matters and the one a count cannot express: removing a day
 * from a schedule changes the fortnight that happens to be generated and every matching day after
 * it. Saying "3 dates" alone would understate that by an unbounded amount.
 */
export function impactWindowLabel(event: ChangeEvent): string | null {
  if (!event.impact_from) return null
  const from = parseISO(event.impact_from)
  if (!isValid(from)) return null

  if (!event.impact_to) {
    return `Applies from ${formatDayMonth(from)} onward — later dates are not generated yet`
  }
  const to = parseISO(event.impact_to)
  if (!isValid(to)) return null
  if (event.impact_from === event.impact_to) return null
  return `Applies ${formatDayMonth(from)} – ${formatDayMonthYear(to)}`
}

// ---------------------------------------------------------------------------
// Grouping and merging
// ---------------------------------------------------------------------------

export interface LineageEntry {
  correlationId: string
  occurredAt: string
  /** The event a person caused. */
  root: ChangeEvent
  /** Everything that followed from it, oldest first. */
  children: ChangeEvent[]
}

/**
 * Group a plan's events into one entry per user action.
 *
 * A single save writes a plan row, a dozen stop rows and a fortnight of instance rows. Rendering
 * them flat would make one decision read as thirty, so they are grouped by `correlation_id` and
 * the row with no parent is the one shown.
 *
 * `sequence_in_action` orders within a group, not `occurred_at`: rows inserted by one statement
 * share a timestamp to the microsecond.
 */
export function groupLineage(events: ChangeEvent[]): LineageEntry[] {
  const byCorrelation = new Map<string, ChangeEvent[]>()
  for (const event of events) {
    const bucket = byCorrelation.get(event.correlation_id)
    if (bucket) bucket.push(event)
    else byCorrelation.set(event.correlation_id, [event])
  }

  const entries: LineageEntry[] = []
  for (const members of byCorrelation.values()) {
    const ordered = [...members].sort((a, b) => a.sequence_in_action - b.sequence_in_action)
    const byId = new Map(ordered.map((e) => [e.id, e]))

    // A root is an event nothing in this group caused. Sharing a correlation id is not enough:
    // the nightly generation puts one id on a whole run, so a fortnight of independently created
    // routes would otherwise read as one action with thirteen children hanging off the first.
    const roots = ordered.filter((e) => !e.parent_event_id || !byId.has(e.parent_event_id))

    for (const root of roots) {
      entries.push({
        correlationId: root.id,
        occurredAt: root.occurred_at,
        root,
        // Only what this root actually caused, directly or transitively.
        children: ordered.filter((e) => e.id !== root.id && descendsFrom(e, root.id, byId)),
      })
    }
  }

  return entries.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
}

/**
 * Split a plan's lineage into changes to the template and changes to single dates.
 *
 * Keyed on the *root's* scope, not each event's. A plan edit's cascade children are instance-scope
 * by nature — that is what a cascade is — and pulling them out would strip a template change of
 * the very rows that say which days it reached. An entry belongs to "individual dates" only when
 * the thing a person acted on was one day.
 *
 * One query still backs both: `planned_route_id` is set on instance rows precisely so a day edited
 * directly is visible from the plan, and separating them at read time keeps that while letting a
 * reader ask the two questions apart.
 */
export function splitLineageByScope(entries: LineageEntry[]): {
  plan: LineageEntry[]
  instance: LineageEntry[]
} {
  const plan: LineageEntry[] = []
  const instance: LineageEntry[] = []
  for (const entry of entries) {
    if (entry.root.scope === "instance") instance.push(entry)
    else plan.push(entry)
  }
  return { plan, instance }
}

/** Walk the parent chain, guarding against a cycle the database cannot rule out on its own. */
function descendsFrom(event: ChangeEvent, rootId: string, byId: Map<string, ChangeEvent>): boolean {
  const seen = new Set<string>([event.id])
  let parentId = event.parent_event_id
  while (parentId) {
    if (parentId === rootId) return true
    if (seen.has(parentId)) return false
    seen.add(parentId)
    parentId = byId.get(parentId)?.parent_event_id ?? null
  }
  return false
}

/** One row on a route's activity timeline, from either source. */
export type TimelineItem =
  | { kind: "change"; at: string; id: string; event: ChangeEvent }
  | { kind: "export"; at: string; id: string; export: RouteExport }

/**
 * A route's edits and its push attempts, in one chronological list.
 *
 * The pushes come from `collections_route_exports`, which already records every attempt per route
 * per system with its error — so a failed Track-POD push belongs in the story at the point it
 * happened rather than in a separate panel. Nothing new is written to achieve this.
 */
export function mergeRouteTimeline(events: ChangeEvent[], exports: RouteExport[]): TimelineItem[] {
  const items: TimelineItem[] = [
    ...events.map((event) => ({
      kind: "change" as const,
      at: event.occurred_at,
      id: event.id,
      event,
    })),
    ...exports.map((entry) => ({
      kind: "export" as const,
      at: entry.attempted_at,
      id: entry.id,
      export: entry,
    })),
  ]
  return items.sort((a, b) => b.at.localeCompare(a.at))
}

/**
 * Which plan or which day an event belongs to, for a feed that mixes both.
 *
 * A plan's history shows the changes somebody made to one date as well as the ones that came down
 * from the template. `entity_label` alone cannot separate them -- it is the instance code, which
 * spells its date as `20260902` in the middle of an identifier -- and a reader must be able to
 * tell a reassignment on one Wednesday from a change to every Wednesday.
 *
 * So an instance-scope row leads with its date. The route code is dropped when it is the subject,
 * because it says the date again in a form nobody reads; a stop's name is kept, because it is the
 * half the date cannot express.
 */
export function subjectLabel(event: ChangeEvent): string | null {
  if (event.scope === "instance" && event.route_instance_date) {
    const day = parseISO(event.route_instance_date)
    if (isValid(day)) {
      const date = formatDayShort(day)
      const named =
        event.entity_label && event.entity_label !== event.route_instance_code
          ? event.entity_label
          : null
      return named ? `${date} · ${named}` : date
    }
  }
  return event.entity_label
}

/** Who did it, in words, with the honest answer when nobody can be named. */
export function actorLabel(event: ChangeEvent): string {
  if (event.actor_kind === "cron") return "Scheduled job"
  if (event.actor_kind === "api_key") return "External system"
  return event.actor_name || event.actor_email || "Unknown"
}

/**
 * The one-word caveat on who did this, or null when there is none.
 *
 * Three provenances a reader should be able to tell apart. A row imported from the old
 * edit-history table carries a name the client supplied and holds a field subset rather than a
 * whole row, so it is marked `imported` rather than `unverified` — the distinction matters,
 * because "unverified" reads as a doubt about the person where "imported" is a fact about the
 * record. A row whose actor came from an unverified token decode is genuinely the former.
 */
export function actorProvenanceNote(event: ChangeEvent): string | null {
  if (event.cause === "backfill") return "imported"
  if (!event.actor_verified && event.actor_kind === "user") return "unverified"
  return null
}

/**
 * A plan event's follow-ons, split by the question they answer.
 *
 * "15 follow-on changes" alone is a dead end when the events carrying the answer are loaded.
 * Most of them are per-date route rows the chip strip already summarises;
 * the few that a reader actually wants are the stop changes, which say *what* moved rather than
 * *when*.
 */
export interface FollowOns {
  /** Which stops were added, removed, reordered or retimed. */
  stops: ChangeEvent[]
  /** Per-date route rows. Redundant with the chips, kept for the count. */
  routeDates: ChangeEvent[]
}

export function splitFollowOns(children: ChangeEvent[]): FollowOns {
  const stops: ChangeEvent[] = []
  const routeDates: ChangeEvent[] = []
  for (const child of children) {
    // The envelope event for a stop sync says only how many; its children say which.
    if (child.event_type === "plan_stops.synced") continue
    if (child.entity_table === "collections_route_stops" || child.event_type.startsWith("stop.")) {
      stops.push(child)
    } else {
      routeDates.push(child)
    }
  }
  return { stops, routeDates }
}

/** One stop's movement, as a line: what it is, what happened, and the times either side. */
export function stopChangeLine(event: ChangeEvent): string {
  const name = event.entity_label ?? rowSummary(event) ?? "Stop"
  const verb = changeEventLabel(event.event_type)
    .replace(/^Stop /, "")
    .replace(/ (from|to) plan$/, "")
    .toLowerCase()

  if (isRowLifecycle(event)) {
    const detail = rowSummary(event)
    return detail && detail !== name ? `${name} · ${verb} · ${detail}` : `${name} · ${verb}`
  }

  const moves = formattedChanges(event)
    .map((c) => `${c.before} → ${c.after}`)
    .join(", ")
  return moves ? `${name} · ${verb} · ${moves}` : `${name} · ${verb}`
}
